import { LayoutGrid, Trophy, BrainCircuit, Search, Moon, Sun, Layers } from 'lucide-react'
import { useTaskStore } from '../../store/taskStore'
import clsx from 'clsx'
import AccessibilitySettings from '../Settings/AccessibilitySettings'
import AuthPanel from '../Auth/AuthPanel'

export type AppSection = 'workspace' | 'progress' | 'graph'

interface Props {
  currentSection: AppSection
  onSelectSection: (section: AppSection) => void
}

export default function NodeNotchSidebar({ currentSection, onSelectSection }: Props) {
  const accessibility = useTaskStore((s) => s.accessibility)
  const updateAccessibility = useTaskStore((s) => s.updateAccessibility)
  const isDark = Boolean(accessibility.darkMode)

  const openPalette = () => {
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }))
  }

  const items: { id: AppSection; label: string; icon: React.ReactNode }[] = [
    {
      id: 'workspace',
      label: 'Workspace (Canvas)',
      icon: <LayoutGrid size={18} />,
    },
    {
      id: 'progress',
      label: 'Progresso RPG',
      icon: <Trophy size={18} />,
    },
    {
      id: 'graph',
      label: 'Second Brain (Grafo)',
      icon: <BrainCircuit size={18} />,
    },
  ]

  return (
    <aside
      className="group relative z-[1000] flex w-14 shrink-0 flex-col items-center justify-between border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm py-4 transition-colors select-none"
      aria-label="Navegação lateral Node Notch"
    >
      {/* Top Section: Brand Node & Main Areas */}
      <div className="flex flex-col items-center gap-5 w-full">
        {/* Brand Icon Node */}
        <div className="group/node relative flex items-center justify-center w-full">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white shadow-md shadow-blue-500/30 transition-transform duration-200 hover:scale-105 cursor-pointer"
            title="TaskPriority Workspace"
          >
            <Layers size={20} />
          </div>
          <span className="absolute left-full ml-3 px-2.5 py-1 text-xs font-semibold text-slate-800 dark:text-slate-100 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-lg shadow-xl border border-slate-200 dark:border-slate-700/80 whitespace-nowrap opacity-0 group-hover/node:opacity-100 group-hover/node:translate-x-0 translate-x-1 transition-all duration-200 pointer-events-none z-[1001]">
            TaskPriority Workspace
          </span>
        </div>

        {/* Navigation Nodes */}
        <nav className="flex flex-col items-center gap-2.5 w-full px-2" aria-label="Nós de navegação">
          {items.map((item) => {
            const active = currentSection === item.id
            return (
              <div key={item.id} className="group/node relative flex items-center justify-center w-full">
                {/* Active Node Notch Bar */}
                {active && (
                  <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-blue-600 shadow-sm shadow-blue-500" />
                )}

                <button
                  type="button"
                  onClick={() => onSelectSection(item.id)}
                  className={clsx(
                    'relative flex h-9 w-9 items-center justify-center rounded-xl text-xs transition-all duration-200 hover:scale-110 hover:-translate-y-0.5 active:scale-95 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
                    active
                      ? 'bg-blue-600/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400 font-semibold shadow-sm'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-200'
                  )}
                  title={item.label}
                  aria-label={item.label}
                >
                  {item.icon}
                </button>

                {/* Sleek Side Tooltip */}
                <span className="absolute left-full ml-3 px-2.5 py-1 text-xs font-semibold text-slate-800 dark:text-slate-100 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-lg shadow-xl border border-slate-200 dark:border-slate-700/80 whitespace-nowrap opacity-0 group-hover/node:opacity-100 group-hover/node:translate-x-0 translate-x-1 transition-all duration-200 pointer-events-none z-[1001]">
                  {item.label}
                </span>
              </div>
            )
          })}
        </nav>
      </div>

      {/* Bottom Section: Quick Action Nodes */}
      <div className="flex flex-col items-center gap-2.5 w-full px-2 border-t border-slate-200 dark:border-slate-800 pt-3">
        {/* Search Node (Ctrl+K) */}
        <div className="group/node relative flex items-center justify-center w-full">
          <button
            type="button"
            onClick={openPalette}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-200 transition-all duration-200 hover:scale-110 hover:-translate-y-0.5 active:scale-95 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
            title="Buscar ou executar comando (Ctrl+K)"
            aria-label="Buscar ou executar comando"
          >
            <Search size={17} />
          </button>
          <span className="absolute left-full ml-3 px-2.5 py-1 text-xs font-semibold text-slate-800 dark:text-slate-100 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-lg shadow-xl border border-slate-200 dark:border-slate-700/80 whitespace-nowrap opacity-0 group-hover/node:opacity-100 group-hover/node:translate-x-0 translate-x-1 transition-all duration-200 pointer-events-none z-[1001]">
            Buscar ou Comandar (Ctrl+K)
          </span>
        </div>

        {/* Preferences */}
        <div className="group/node relative flex items-center justify-center w-full">
          <AccessibilitySettings iconOnly />
          <span className="absolute left-full ml-3 px-2.5 py-1 text-xs font-semibold text-slate-800 dark:text-slate-100 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-lg shadow-xl border border-slate-200 dark:border-slate-700/80 whitespace-nowrap opacity-0 group-hover/node:opacity-100 group-hover/node:translate-x-0 translate-x-1 transition-all duration-200 pointer-events-none z-[1001]">
            Preferências de Acessibilidade
          </span>
        </div>

        {/* Auth / Account */}
        <div className="group/node relative flex items-center justify-center w-full">
          <AuthPanel iconOnly />
          <span className="absolute left-full ml-3 px-2.5 py-1 text-xs font-semibold text-slate-800 dark:text-slate-100 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-lg shadow-xl border border-slate-200 dark:border-slate-700/80 whitespace-nowrap opacity-0 group-hover/node:opacity-100 group-hover/node:translate-x-0 translate-x-1 transition-all duration-200 pointer-events-none z-[1001]">
            Conta / Autenticação
          </span>
        </div>

        {/* Theme Toggle Node */}
        <div className="group/node relative flex items-center justify-center w-full">
          <button
            type="button"
            onClick={() => updateAccessibility({ darkMode: !isDark })}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-200 transition-all duration-200 hover:scale-110 hover:-translate-y-0.5 active:scale-95 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
            title={isDark ? 'Ativar Modo Claro' : 'Ativar Modo Escuro'}
            aria-label={isDark ? 'Ativar Modo Claro' : 'Ativar Modo Escuro'}
          >
            {isDark ? <Sun size={17} className="text-yellow-400" /> : <Moon size={17} />}
          </button>
          <span className="absolute left-full ml-3 px-2.5 py-1 text-xs font-semibold text-slate-800 dark:text-slate-100 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-lg shadow-xl border border-slate-200 dark:border-slate-700/80 whitespace-nowrap opacity-0 group-hover/node:opacity-100 group-hover/node:translate-x-0 translate-x-1 transition-all duration-200 pointer-events-none z-[1001]">
            {isDark ? 'Alternar para Modo Claro' : 'Alternar para Modo Escuro'}
          </span>
        </div>
      </div>
    </aside>
  )
}

